import { useMemo, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { LoaderCircle } from 'lucide-react'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DateTimePicker } from '@/components/DateTimePicker'
import { FieldLabel } from '@/components/FieldLabel'
import {
  ELECTIONS_QUERY_KEY,
  ElectionSelect,
  isRecent,
} from '@/components/political/ElectionSelect'
import { PoliticalPreviewFrame } from '@/components/political/PoliticalPreviewFrame'
import { AuthService } from '@/services/core/auth'
import { apiErrorDescription } from '@/services/core/apiError'
import { PreviewApi, type PreviewRequest } from '@/services/creatives/previewApi'
import { ElectionsApi } from '@/services/political/electionsApi'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/lib/utils'
import { withScheme } from '@/utils/url'
import {
  PREVIEW_LANGUAGES,
  politicalPreviewTemplate,
  preparePoliticalPreviewHtml,
  resolvePreviewLanguage,
  type PoliticalPreviewLayout,
} from '@/utils/politicalPreview'

const DEFAULT_IMAGE_URL =
  'https://assets.adnuntius.com/cgaeda/67b69k75g97mrym79mwqhspyylp3nq8ph6rzx5fj83hb563ztc.png'

type ElectionMode = 'existing' | 'manual'
type ImageSource = 'hosted' | 'upload'

interface PreviewForm {
  electionMode: ElectionMode
  electionName: string
  electionDate: string
  sponsorName: string
  controllingEntityName: string
  controllingSameAsSponsor: boolean
  hasUserTargeting: boolean
  layout: PoliticalPreviewLayout
  imageSource: ImageSource
  hostedImageUrl: string
  upload: { id: string; content: string; name: string; mimeType: string } | null
  destinationUrl: string
  width: number | null
  height: number | null
}

/** A preview the form is complete enough to render. */
interface Draft {
  request: PreviewRequest
  imageUrl: string
  imageKey: string
  destinationUrl: string
  layout: PoliticalPreviewLayout
  imageSource: ImageSource
}

function imageUrlOf(form: PreviewForm): string {
  if (form.imageSource === 'upload') return form.upload?.content ?? ''
  return form.hostedImageUrl
}

/** Loads an image to learn its natural size. */
function imageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight })
    image.onerror = reject
    image.src = url
  })
}

/**
 * Public page showing how an image ad looks with the political transparency
 * overlay required by the EU's TTPA regulation. Signed-in users can pick one
 * of the network's elections; anyone else enters the details by hand.
 */
export function PoliticalPreviewPage() {
  const { t } = useTranslation()
  const signedIn = AuthService.isAuthenticated()

  const [form, setForm] = useState<PreviewForm>({
    electionMode: signedIn ? 'existing' : 'manual',
    electionName: '',
    electionDate: '',
    sponsorName: 'United Nations',
    controllingEntityName: 'United Nations',
    controllingSameAsSponsor: true,
    hasUserTargeting: false,
    layout: 'standard',
    imageSource: 'hosted',
    hostedImageUrl: DEFAULT_IMAGE_URL,
    upload: null,
    destinationUrl: 'https://example.com',
    width: 400,
    height: 400,
  })
  const update = (patch: Partial<PreviewForm>) => setForm((prev) => ({ ...prev, ...patch }))

  const [chosenElectionId, setChosenElectionId] = useState<string | null>(null)
  const [language, setLanguage] = useState<string>('browser')
  const [imageError, setImageError] = useState('')

  const elections = useQuery({
    queryKey: ELECTIONS_QUERY_KEY,
    queryFn: ElectionsApi.getAll,
    staleTime: 5 * 60 * 1000,
    enabled: signedIn,
  })

  // Until one is picked, preselect the first election still to be held
  const defaultElectionId = useMemo(() => {
    const upcoming = (elections.data ?? [])
      .filter((e) => !e.testOnly && isRecent(e))
      .sort(
        (a, b) =>
          Number(!a.electionDate) - Number(!b.electionDate) ||
          a.electionDate.localeCompare(b.electionDate) ||
          a.name.toLowerCase().localeCompare(b.name.toLowerCase())
      )
    return upcoming[0]?.id ?? ''
  }, [elections.data])
  const electionId = chosenElectionId ?? defaultElectionId
  const selectedElection = elections.data?.find((e) => e.id === electionId)

  // What the form describes right now: a preview to render, or why it cannot be
  const draft = useMemo((): { error: string } | { preview: Draft } => {
    const fail = (key: string) => ({ error: t(`politicalPreview.errors.${key}`) })
    if (form.electionMode === 'existing') {
      if (!electionId) return fail('selectElection')
    } else if (!form.electionName.trim()) {
      return fail('electionNameRequired')
    }
    const sponsor = form.sponsorName.trim()
    if (!sponsor) return fail('sponsorRequired')
    const controller = form.controllingSameAsSponsor ? sponsor : form.controllingEntityName.trim()
    if (!controller) return fail('controllingEntityRequired')
    if (!form.destinationUrl.trim()) return fail('destinationRequired')
    if (!form.width || form.width < 1) return fail('widthRequired')
    if (!form.height || form.height < 1) return fail('heightRequired')
    const imageUrl = imageUrlOf(form)
    if (!imageUrl) return fail('imageRequired')

    const election =
      form.electionMode === 'existing'
        ? { name: selectedElection?.name ?? '', date: selectedElection?.electionDate ?? '' }
        : { name: form.electionName.trim(), date: form.electionDate }
    const image = { cdnId: imageUrl }
    return {
      preview: {
        imageUrl,
        // An upload is a data URL of the whole file — too big to hash into a query key
        imageKey: form.imageSource === 'upload' ? `upload:${form.upload?.id}` : imageUrl,
        destinationUrl: form.destinationUrl,
        layout: form.layout,
        imageSource: form.imageSource,
        request: {
          creativeWidth: form.width,
          creativeHeight: form.height,
          lineItemCode: 'preview-line-item',
          advertiserName: sponsor,
          controllingEntityName: controller,
          electionName: election.name,
          electionDate: election.date,
          hasUserTargeting: form.hasUserTargeting,
          hasContextualTargeting: false,
          hasLocationTargeting: false,
          renderTemplate: politicalPreviewTemplate(form.layout),
          layoutParameters: { destination: form.destinationUrl, Image: image, image },
        },
      },
    }
  }, [form, electionId, selectedElection, t])

  // Re-render once typing pauses, rather than on every keystroke
  const pending = useDebouncedValue(draft, 500)
  const next = 'preview' in pending ? pending.preview : null

  const preview = useQuery({
    queryKey: [
      'political-preview',
      next && { ...next.request, layoutParameters: null, imageUrl: null, imageKey: next.imageKey },
      language,
    ],
    queryFn: async () => {
      const html = await PreviewApi.render(next!.request)
      return {
        html: preparePoliticalPreviewHtml(
          html,
          next!.destinationUrl,
          next!.imageUrl,
          resolvePreviewLanguage(language)
        ),
        width: next!.request.creativeWidth,
        height: next!.request.creativeHeight,
        fullScreen: next!.layout === 'fullScreen',
        imageSource: next!.imageSource,
      }
    },
    enabled: !!next,
    // Keep showing the last preview while the next renders, or while the form is incomplete
    placeholderData: keepPreviousData,
    retry: false,
    gcTime: 0,
  })

  // An election cannot be picked before the list arrives, so that is not an error yet
  const validationError =
    'error' in draft && !(form.electionMode === 'existing' && elections.isLoading)
      ? draft.error
      : ''

  async function loadImageSize(url: string): Promise<boolean> {
    try {
      const size = await imageSize(url)
      setImageError('')
      update({ width: size.width || form.width, height: size.height || form.height })
      return true
    } catch {
      setImageError(t('politicalPreview.errors.imageLoadFailed'))
      return false
    }
  }

  function onUpload(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target
    const file = input.files?.[0]
    setImageError('')
    if (!file) return
    const reader = new FileReader()
    reader.onload = async () => {
      const content = typeof reader.result === 'string' ? reader.result : ''
      if (content && (await loadImageSize(content))) {
        const id = `${file.name}:${file.size}:${file.lastModified}`
        update({ upload: { id, content, name: file.name, mimeType: file.type } })
      }
    }
    reader.onerror = () => setImageError(t('politicalPreview.errors.uploadReadFailed'))
    reader.readAsDataURL(file)
    input.value = ''
  }

  const shown = preview.data
  const rendering = preview.isFetching

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8">
        <header className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold">{t('politicalPreview.title')}</h1>
          <p className="max-w-3xl text-sm text-(--ad-text-muted)">{t('politicalPreview.intro')}</p>
        </header>

        <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <div className="flex flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('politicalPreview.politicalDetails')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {signedIn && (
                  <div className="p-field">
                    <FieldLabel>{t('politicalPreview.electionMode')}</FieldLabel>
                    <RadioGroup
                      value={form.electionMode}
                      onValueChange={(v) => update({ electionMode: v as ElectionMode })}
                      className="flex flex-wrap gap-4"
                    >
                      <Label className="font-normal">
                        <RadioGroupItem value="existing" />
                        {t('politicalPreview.electionModeExisting')}
                      </Label>
                      <Label className="font-normal">
                        <RadioGroupItem value="manual" />
                        {t('politicalPreview.electionModeManual')}
                      </Label>
                    </RadioGroup>
                  </div>
                )}

                {form.electionMode === 'existing' ? (
                  <div className="p-field">
                    <FieldLabel>{t('politicalPreview.election')}</FieldLabel>
                    <ElectionSelect value={electionId} onChange={setChosenElectionId} />
                    {elections.isError && (
                      <p className="text-xs text-destructive">
                        {t('politicalPreview.electionsLoadFailed')}{' '}
                        <Button
                          type="button"
                          variant="link"
                          size="sm"
                          className="h-auto p-0 text-xs"
                          onClick={() => elections.refetch()}
                        >
                          {t('politicalPreview.retry')}
                        </Button>
                      </p>
                    )}
                    {elections.isSuccess && elections.data.length === 0 && (
                      <p className="text-xs text-(--ad-text-muted)">
                        {t('politicalPreview.noElections')}
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="p-field">
                      <FieldLabel htmlFor="pp-election-name">
                        {t('politicalPreview.electionName')}
                      </FieldLabel>
                      <Input
                        id="pp-election-name"
                        value={form.electionName}
                        placeholder={t('politicalPreview.electionNamePlaceholder')}
                        onChange={(e) => update({ electionName: e.target.value })}
                      />
                    </div>
                    <div className="p-field">
                      <FieldLabel hint={t('politicalPreview.electionDateHint')}>
                        {t('politicalPreview.electionDate')}
                      </FieldLabel>
                      <DateTimePicker
                        dateOnly
                        value={form.electionDate}
                        onChange={(electionDate) => update({ electionDate })}
                      />
                    </div>
                  </>
                )}

                <div className="p-field">
                  <FieldLabel htmlFor="pp-sponsor">{t('politicalPreview.sponsorName')}</FieldLabel>
                  <Input
                    id="pp-sponsor"
                    value={form.sponsorName}
                    placeholder={t('politicalPreview.sponsorPlaceholder')}
                    onChange={(e) => update({ sponsorName: e.target.value })}
                  />
                </div>

                <div className="p-field">
                  <FieldLabel htmlFor="pp-controlling">
                    {t('politicalPreview.controllingEntity')}
                  </FieldLabel>
                  <Input
                    id="pp-controlling"
                    value={
                      form.controllingSameAsSponsor ? form.sponsorName : form.controllingEntityName
                    }
                    placeholder={t('politicalPreview.sponsorPlaceholder')}
                    disabled={form.controllingSameAsSponsor}
                    onChange={(e) => update({ controllingEntityName: e.target.value })}
                  />
                  <Label className="text-xs font-normal">
                    <Checkbox
                      checked={form.controllingSameAsSponsor}
                      onCheckedChange={(checked) =>
                        update({
                          controllingSameAsSponsor: checked === true,
                          controllingEntityName: form.sponsorName,
                        })
                      }
                    />
                    {t('politicalPreview.sameAsSponsor')}
                  </Label>
                </div>

                <div className="p-field">
                  <FieldLabel>{t('politicalPreview.targeting')}</FieldLabel>
                  <Label className="font-normal">
                    <Checkbox
                      checked={form.hasUserTargeting}
                      onCheckedChange={(checked) => update({ hasUserTargeting: checked === true })}
                    />
                    {t('politicalPreview.userTargeting')}
                  </Label>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('politicalPreview.creativeContent')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div className="p-field">
                  <FieldLabel>{t('politicalPreview.layout')}</FieldLabel>
                  <RadioGroup
                    value={form.layout}
                    onValueChange={(v) => update({ layout: v as PoliticalPreviewLayout })}
                    className="flex flex-wrap gap-4"
                  >
                    <Label className="font-normal">
                      <RadioGroupItem value="standard" />
                      {t('politicalPreview.layoutStandard')}
                    </Label>
                    <Label className="font-normal">
                      <RadioGroupItem value="fullScreen" />
                      {t('politicalPreview.layoutFullScreen')}
                    </Label>
                  </RadioGroup>
                </div>

                <div className="p-field">
                  <FieldLabel>{t('politicalPreview.imageSource')}</FieldLabel>
                  <RadioGroup
                    value={form.imageSource}
                    onValueChange={(v) => update({ imageSource: v as ImageSource })}
                    className="flex flex-wrap gap-4"
                  >
                    <Label className="font-normal">
                      <RadioGroupItem value="hosted" />
                      {t('politicalPreview.imageHosted')}
                    </Label>
                    <Label className="font-normal">
                      <RadioGroupItem value="upload" />
                      {t('politicalPreview.imageUpload')}
                    </Label>
                  </RadioGroup>
                </div>

                {form.imageSource === 'hosted' ? (
                  <div className="p-field">
                    <FieldLabel htmlFor="pp-image-url">
                      {t('politicalPreview.imageHosted')}
                    </FieldLabel>
                    <Input
                      id="pp-image-url"
                      type="url"
                      value={form.hostedImageUrl}
                      placeholder="https://cdn.example.org/ad.jpg"
                      onChange={(e) => update({ hostedImageUrl: e.target.value })}
                      onBlur={(e) => update({ hostedImageUrl: withScheme(e.target.value) })}
                    />
                    {form.hostedImageUrl && (
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto self-start p-0 text-xs"
                        onClick={() => loadImageSize(form.hostedImageUrl)}
                      >
                        {t('politicalPreview.loadDimensions')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="p-field">
                    <FieldLabel htmlFor="pp-upload">{t('politicalPreview.uploadImage')}</FieldLabel>
                    {form.upload ? (
                      <div className="flex flex-col gap-1 rounded-md border bg-(--ad-neutral-soft) p-3 text-sm">
                        <span className="break-words">
                          <strong>{t('politicalPreview.uploadedFile')}:</strong> {form.upload.name}
                        </span>
                        <span>
                          <strong>{t('politicalPreview.mimeType')}:</strong> {form.upload.mimeType}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-1 self-start"
                          onClick={() => update({ upload: null })}
                        >
                          {t('politicalPreview.clearUpload')}
                        </Button>
                      </div>
                    ) : (
                      <Input id="pp-upload" type="file" accept="image/*" onChange={onUpload} />
                    )}
                  </div>
                )}

                <div className="p-field">
                  <FieldLabel htmlFor="pp-destination">
                    {t('politicalPreview.destinationUrl')}
                  </FieldLabel>
                  <Input
                    id="pp-destination"
                    type="url"
                    value={form.destinationUrl}
                    placeholder="https://example.org/campaign"
                    onChange={(e) => update({ destinationUrl: e.target.value })}
                    onBlur={(e) => update({ destinationUrl: withScheme(e.target.value) })}
                  />
                </div>

                <div className="p-field">
                  <FieldLabel htmlFor="pp-width">{t('politicalPreview.dimensions')}</FieldLabel>
                  <div className="flex items-center gap-2">
                    <NumberInput
                      id="pp-width"
                      aria-label={t('politicalPreview.width')}
                      min={1}
                      value={form.width}
                      onChange={(width) => update({ width })}
                    />
                    <span aria-hidden>×</span>
                    <NumberInput
                      aria-label={t('politicalPreview.height')}
                      min={1}
                      value={form.height}
                      onChange={(height) => update({ height })}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {imageError && (
              <Alert variant="destructive">
                <AlertDescription>{imageError}</AlertDescription>
              </Alert>
            )}
            {validationError && (
              <Alert variant="destructive">
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}
          </div>

          <Card>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2">
                {t('politicalPreview.preview')}
                {rendering && (
                  <LoaderCircle
                    className="size-4 animate-spin text-(--ad-text-muted)"
                    aria-label={t('politicalPreview.generating')}
                  />
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Label htmlFor="pp-language" className="text-xs text-(--ad-text-muted)">
                  {t('politicalPreview.language')}
                </Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger id="pp-language" size="sm" className="w-56">
                    <SelectValue>{t(`politicalPreview.languages.${language}`)}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {PREVIEW_LANGUAGES.map((code) => (
                      <SelectItem key={code} value={code}>
                        {t(`politicalPreview.languages.${code}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              {rendering && !shown && (
                <p className="text-sm text-(--ad-text-muted)">{t('politicalPreview.generating')}</p>
              )}
              {preview.isError && (
                <Alert variant="destructive">
                  <AlertDescription>
                    {apiErrorDescription(preview.error) ||
                      t('politicalPreview.errors.renderFailed')}
                  </AlertDescription>
                </Alert>
              )}
              {shown && (
                <div className={cn('transition-opacity', rendering && 'opacity-60')}>
                  <PoliticalPreviewFrame
                    key={preview.dataUpdatedAt}
                    html={shown.html}
                    width={shown.width}
                    height={shown.height}
                    fullScreen={shown.fullScreen}
                    imageSourceLabel={
                      shown.imageSource === 'upload'
                        ? t('politicalPreview.imageSourceUploaded')
                        : t('politicalPreview.imageHosted')
                    }
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
